import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Loader2 } from "lucide-react";

// Admin-only one-shot load-test cleanup: dry-run (counts) then confirm to soft-archive.
export function LoadtestCleanupButton() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (dry_run) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup/loadtest", { dry_run });
      if (dry_run) { setPreview(data); setResult(null); }
      else { setResult(data); setPreview(null); toast.success("Load-test cleanup complete"); }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Cleanup failed"); }
    finally { setBusy(false); }
  };

  const sum = (o) => Object.values(o || {}).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="loadtest-cleanup" className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold text-slate-900">Load-test data cleanup</p>
          <p className="text-xs text-slate-500">Soft-archive the @loadtest.arps / @demo.arps cohort. Restorable; skips anything tied to real money.</p>
        </div>
        <Button data-testid="cleanup-dryrun-btn" size="sm" variant="outline" disabled={busy} onClick={() => run(true)}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Preview counts
        </Button>
      </div>

      {preview && (
        <div data-testid="cleanup-preview" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <p className="font-semibold text-amber-900">Dry-run — {preview.cohort_users} cohort users</p>
          <p className="mt-1 text-amber-800">Archivable: {sum(preview.archivable)} records ({preview.archivable.users} users, {preview.archivable.orders} orders, {preview.archivable.invoices} invoices, {preview.archivable.files} files).</p>
          <p className="text-amber-800">Skipped (needs-review): {preview.skipped_counts.users} users, {preview.skipped_counts.orders} orders.</p>
          {(preview.skipped.users || []).map((s, i) => (
            <p key={`u${i}`} data-testid="cleanup-skipped-user" className="text-xs text-amber-700">· {s.email} — {s.reason}</p>
          ))}
          {(preview.skipped.orders || []).map((s, i) => (
            <p key={`o${i}`} data-testid="cleanup-skipped-order" className="text-xs text-amber-700">· {s.job_number} — {s.reason}</p>
          ))}
          <div className="mt-3">
            <Button data-testid="cleanup-confirm-btn" size="sm" disabled={busy} onClick={() => run(false)} className="bg-red-600 text-white hover:bg-red-700">
              <Trash2 className="mr-1 h-4 w-4" /> Archive these now
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div data-testid="cleanup-result" className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          <p className="font-semibold">Archived {sum(result.archived)} records.</p>
          <p className="text-xs">users {result.archived.users} · orders {result.archived.orders} · invoices {result.archived.invoices} · files {result.archived.files}</p>
          <p className="mt-1 text-xs">Backup id: <span className="font-mono">{result.backup_id}</span> (collection cleanup_backups)</p>
          {sum(result.skipped_counts) > 0 && (
            <div className="mt-2 border-t border-emerald-200 pt-2">
              <p className="text-xs font-semibold">Skipped (left active): {result.skipped_counts.users} users, {result.skipped_counts.orders} orders</p>
              {(result.skipped.users || []).map((s, i) => (
                <p key={`u${i}`} data-testid="cleanup-result-skipped-user" className="text-xs">· {s.email} — {s.reason}</p>
              ))}
              {(result.skipped.orders || []).map((s, i) => (
                <p key={`o${i}`} data-testid="cleanup-result-skipped-order" className="text-xs">· {s.job_number} — {s.reason}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
